/**
 * SchedulerConfigPage Component
 *
 * Main page for scheduler configuration management with tabbed interface.
 * Orchestrates service areas, blackout dates, and overview components.
 *
 * Features:
 * - Tabbed interface (Overview | Service Areas | Blackout Dates)
 * - URL-based tab state (supports deep linking)
 * - Page header with navigation breadcrumbs
 * - Consistent layout with other admin pages
 *
 * Routes:
 * - /scheduler - Default to Overview tab
 * - /scheduler?tab=service-areas - Service Areas tab
 * - /scheduler?tab=blackout-dates - Blackout Dates tab
 * - /scheduler?tab=overview - Overview tab
 *
 * @example
 * <SchedulerConfigPage />
 */

import React from 'react'
import { useSearchParams } from 'react-router-dom'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Calendar } from 'lucide-react'
import { ServiceAreaList } from '../components/ServiceAreaList'
import { BlackoutDatesList } from '../components/BlackoutDatesList'
import { SchedulerConfigOverview } from '../components/SchedulerConfigOverview'

type TabValue = 'overview' | 'service-areas' | 'blackout-dates'

export default function SchedulerConfigPage() {
  const [searchParams, setSearchParams] = useSearchParams()

  // Get tab from URL or default to 'overview'
  const activeTab = (searchParams.get('tab') as TabValue) || 'overview'

  // Update URL when tab changes
  const handleTabChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('tab', value)
    setSearchParams(newParams)
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Calendar className="h-8 w-8 text-primary" />
            Scheduler Configuration
          </h1>
          <p className="text-muted-foreground">
            Manage service areas and blackout dates for scheduling automation
          </p>
        </div>
      </div>

      {/* Tabbed Interface */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="service-areas">Service Areas</TabsTrigger>
          <TabsTrigger value="blackout-dates">Blackout Dates</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6 mt-6">
          <SchedulerConfigOverview />
        </TabsContent>

        {/* Service Areas Tab */}
        <TabsContent value="service-areas" className="space-y-6 mt-6">
          <ServiceAreaList />
        </TabsContent>

        {/* Blackout Dates Tab */}
        <TabsContent value="blackout-dates" className="space-y-6 mt-6">
          <BlackoutDatesList />
        </TabsContent>
      </Tabs>
    </div>
  )
}
